import { useState, useEffect, useRef } from 'react';

/**
 * Hook to track unit repositioning and manage movement animations
 * Detects when units move from one zone/position to another and animates the movement
 */
export const useUnitRepositionAnimations = (gameState, userId) => {
  const [repositioningUnits, setRepositioningUnits] = useState(new Map());
  const previousPositionsRef = useRef(new Map());
  const animationTimeoutRef = useRef(new Map());

  // ✅ CRITICAL FIX: Clear removal timeouts ONLY on unmount, not when effect re-runs.
  // If we clear timeouts in the effect cleanup, any follow-up gameState update (e.g. Phase Traverse
  // "get another order", or WebSocket re-send) causes the cleanup to run and cancels the 2s timeout
  // that removes the unit from repositioningUnits. The unit then stays "repositioning" forever and
  // remains invisible (getVisibleUnit returns null) and untargetable.
  useEffect(() => {
    return () => {
      animationTimeoutRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      animationTimeoutRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    const currentPositions = new Map();
    const allPlayerIds = gameState.players || Object.keys(gameState.state);

    // Build map of current unit positions
    // ✅ CRITICAL FIX: Use array index (idx) instead of unit.position property
    // The DOM slots use array index (data-slot-index), not unit.position
    // Array index matches the actual slot position in the DOM, which is what we need for animation
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit, idx) => {
            if (unit && unit.instanceId) {
              const key = `${playerId}_${unit.instanceId}`;
              // ✅ CRITICAL: Use array index (idx) for animation, not unit.position
              // The DOM slots are rendered using array index, so we must use idx to find the correct slot
              currentPositions.set(key, {
                playerId,
                zone,
                position: idx, // Use array index - this matches data-slot-index in DOM
                arrayIndex: idx, // Same as position for clarity
                instanceId: unit.instanceId
              });
            }
          });
        }
      });
    });

    // Detect repositioned units (same instanceId, different zone or position)
    const newRepositions = new Map();
    currentPositions.forEach((currentPos, key) => {
      const previousPos = previousPositionsRef.current.get(key);
      
      // ✅ FIX: Only detect reposition if zone OR position property actually changed
      // Don't trigger on array index changes (which happen when new units are deployed)
      if (previousPos && 
          previousPos.instanceId === currentPos.instanceId &&
          (previousPos.zone !== currentPos.zone || previousPos.position !== currentPos.position)) {
        
        console.log(`🔄 REPOSITIONING HOOK: Detected reposition for ${key}: ${previousPos.zone}[${previousPos.position}] → ${currentPos.zone}[${currentPos.position}]`);
        
        newRepositions.set(key, {
          instanceId: currentPos.instanceId,
          fromZone: previousPos.zone,
          fromIndex: previousPos.position, // Array index - matches data-slot-index in DOM
          toZone: currentPos.zone,
          toIndex: currentPos.position, // Array index - matches data-slot-index in DOM
          // Keep full playerId (do not derive from key split; IDs can contain underscores).
          playerId: currentPos.playerId
        });

        // Clear any in-flight timeout for this unit before scheduling a new one.
        // This prevents stale timers from removing/re-adding reposition state mid-animation.
        const existingTimeoutId = animationTimeoutRef.current.get(key);
        if (existingTimeoutId) {
          clearTimeout(existingTimeoutId);
        }
        
        // Set timeout to remove from repositioning after animation completes
        const timeoutId = setTimeout(() => {
          setRepositioningUnits(prev => {
            const next = new Map(prev);
            next.delete(key);
            return next;
          });
          animationTimeoutRef.current.delete(key);
        }, 2000); // Animation duration (2 seconds) + small buffer
        
        animationTimeoutRef.current.set(key, timeoutId);
      }
    });

    // Update repositioning units
    if (newRepositions.size > 0) {
      setRepositioningUnits(prev => {
        const next = new Map(prev);
        newRepositions.forEach((reposition, key) => {
          next.set(key, reposition);
        });
        return next;
      });
    }

    // Update previous positions reference
    previousPositionsRef.current = currentPositions;

    // Do NOT clear removal timeouts here. Clearing them on every gameState change cancels
    // the 2s timeout and leaves the unit in repositioningUnits forever (invisible). Cleanup
    // of timeouts is done only on unmount by the effect above.
  }, [gameState, userId]);

  /**
   * Check if a unit is currently being repositioned (for suppressing damage animations and hiding units)
   */
  const isUnitRepositioning = (playerId, instanceId) => {
    const key = `${playerId}_${instanceId}`;
    return repositioningUnits.has(key);
  };

  return {
    isUnitRepositioning,
    repositioningUnits, // Export the map so BattleUI can access it for overlay rendering
  };
};

